import { useEffect, useRef } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ProfileCompletionDialog } from "@/components/ProfileCompletionDialog";
import { PwaInstallBanner } from "@/components/pwa-install-banner";
import { BottomNav } from "@/components/BottomNav";
import { FloatingActionButton } from "@/components/FloatingActionButton";
import { CreateFAB } from "@/components/ui/create-fab";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { MobileUserMenu } from "@/components/MobileUserMenu";
import { BrandWordmark } from "@/components/BrandWordmark";
import GlobalSearch from "@/components/GlobalSearch";
import { CreditBalancePill } from "@/components/CreditBalancePill";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useNotificationsWebSocket } from "@/hooks/use-notifications-ws";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import { useLocation, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { LoadingState } from "@/components/ui/loading-state";
import { useIsMobile } from "@/hooks/use-mobile";

/** Routes that unauthenticated users can browse (read-only). */
const PUBLIC_BROWSE_PATHS = [
  "/festival-guide",
  "/festivals",
  "/discover",
  "/explore",
  "/swipe",
  "/trip/",
  "/routes",
  "/route/",
  "/local-events",
  "/the-scene",
  "/endeavor",
  "/suit-and-tie",
];

function isPublicBrowsePath(path: string): boolean {
  return PUBLIC_BROWSE_PATHS.some(
    (p) => path === p || (p.endsWith("/") && path.startsWith(p)),
  );
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isLoading, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  useNotificationsWebSocket();
  const { openSignupDialog, openLoginDialog } = useSignupDialog();
  const [location, setLocation] = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const isPublicPage = isPublicBrowsePath(location);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublicPage) {
      // Preserve the user's intent so they land back on the page they wanted
      // after sign-in / sign-up, instead of getting stranded on the landing page.
      openSignupDialog({ returnTo: location });
    }
  }, [isLoading, isAuthenticated, isPublicPage, location, openSignupDialog]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <LoadingState variant="spinner" />
      </div>
    );
  }

  // Authenticated users get the full app layout
  if (isAuthenticated) {
    const style = {
      "--sidebar-width": "16rem",
      "--sidebar-width-icon": "3rem",
    };

    return (
      <SidebarProvider style={style as React.CSSProperties}>
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <div className="flex h-screen w-full">
          {!isMobile && <AppSidebar />}
          <div className="flex flex-col flex-1 overflow-hidden">
            <header aria-label="App toolbar" className="flex items-center justify-between p-2 sm:p-4 border-b bg-background">
              <div className="hidden sm:flex items-center gap-2">
                <SidebarTrigger data-testid="button-sidebar-toggle" />
                <Link href="/dashboard" className="flex items-center gap-2" data-testid="link-header-home">
                  <div className="flex flex-col leading-tight min-w-0">
                    <BrandWordmark size="sm" />
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-navy" />
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-gold" />
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-coral" />
                    </div>
                  </div>
                </Link>
              </div>
              <div className="sm:hidden flex items-center gap-2">
                <MobileUserMenu />
                <Link href="/dashboard" className="flex items-center gap-2" data-testid="link-mobile-header-home">
                  <div className="flex flex-col leading-tight min-w-0">
                    <BrandWordmark size="sm" />
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-navy" />
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-gold" />
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-coral" />
                    </div>
                  </div>
                </Link>
              </div>
              <div className="flex items-center gap-1">
                <CreditBalancePill />
                <GlobalSearch />
                <span className="hidden sm:inline-flex">
                  <LanguageSwitcher />
                </span>
                <NotificationBell />
                <ThemeToggle />
              </div>
            </header>
            <main id="main-content" ref={mainRef} className="flex-1 overflow-auto pb-16 sm:pb-0">
              <ErrorBoundary>{children}</ErrorBoundary>
              <AppFooter />
            </main>
          </div>
        </div>
        <ProfileCompletionDialog />
        <PwaInstallBanner />
        <BottomNav />
        <FloatingActionButton />
        <CreateFAB />
      </SidebarProvider>
    );
  }

  // Unauthenticated on a public page — simplified layout with sign-up CTA
  if (isPublicPage) {
    return (
      <div className="flex flex-col h-screen w-full">
        <header
          aria-label="Public toolbar"
          className="flex items-center justify-between px-4 py-3 border-b bg-background sticky top-0 z-50"
        >
          <Link href="/" className="flex items-center gap-2 no-underline">
            <OptimizedImage
              src="/troopn-logo.png"
              alt="Troopn"
              width={28}
              height={28}
              loading="eager"
              className="w-7 h-7 rounded-full"
            />
            <BrandWordmark size="sm" />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openLoginDialog()}
              data-testid="public-login-button"
            >
              {t("signup.logIn", "Log in")}
            </Button>
            <Button
              size="sm"
              onClick={() => openSignupDialog()}
              data-testid="public-signup-button"
            >
              {t("signup.signUpFree", "Sign Up Free")}
            </Button>
          </div>
        </header>
        <main id="main-content" ref={mainRef} className="flex-1 overflow-auto">
          <ErrorBoundary>{children}</ErrorBoundary>
          <AppFooter />
        </main>
      </div>
    );
  }

  // Unauthenticated on a non-public page — redirect already happened
  return null;
}
